import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Label, Select } from "@/components/ui/form";
import { Card, CardBody } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

const ENTITY_TYPES = ["rfq", "line_item", "seller_quote", "po"] as const;

interface FieldDef {
  id: string;
  entityType: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: string;
}

export function AdminCustomFieldsPage() {
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>("rfq");
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");

  const load = () =>
    apiFetch<{ fields: FieldDef[] }>(`/custom-fields?entityType=${entityType}`).then((d) =>
      setFields(d.fields),
    );

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed")).finally(() => setLoading(false));
  }, [entityType]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await apiFetch("/custom-fields", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entityType, fieldLabel: label }),
    });
    setLabel("");
    await load();
  };

  const remove = async (id: string) => {
    await apiFetch(`/custom-fields/${id}`, { method: "DELETE" });
    await load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div>
      <PageHeader title="Custom Fields" subtitle="Add fields at each stage of the RFQ workflow." />
      <Card className="mb-6 max-w-lg">
        <CardBody className="space-y-4">
          <div>
            <Label>Entity type</Label>
            <Select value={entityType} onChange={(e) => setEntityType(e.target.value as typeof entityType)} className="max-w-xs">
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
          <form onSubmit={create} className="flex gap-2">
            <Input placeholder="Field label" value={label} onChange={(e) => setLabel(e.target.value)} required />
            <Button type="submit">Add</Button>
          </form>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          <ul className="divide-y divide-gray-50">
            {fields.length === 0 ? (
              <li className="p-6 text-argon-secondary text-sm text-center">No custom fields.</li>
            ) : fields.map((f) => (
              <li key={f.id} className="px-6 py-4 flex justify-between items-center text-sm">
                <span className="text-argon-dark font-medium">
                  {f.fieldLabel} <span className="text-argon-secondary font-normal">({f.fieldKey})</span>
                </span>
                <Button variant="ghost" size="sm" type="button" onClick={() => void remove(f.id)}>Remove</Button>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
